import type { ActionPayload, DashboardSettings, HuntType, ResourceKey, UnitKey } from '../domain/types';
export type { DashboardSettings, HuntType, ResourceKey, UnitKey } from '../domain/types';

export const defaultDashboardSettings: DashboardSettings = {
  autoBotModules: { collect: true, train_troops: true, auto_hunt: false },
  autoBotHelp: true,
  autoBotHeal: false,
  autoBotShield: false,
  collect: {
    resources: ['hierro', 'carbon', 'madera', 'carne'],
    level: 5,
    marches: 4
  },
  training: {
    units: { infantry: true, lancer: true, marksman: true },
    amountPercent: 100
  },
  hunt: {
    huntType: 'beast',
    huntLevel: 1,
    marches: 1,
    rallyMinutes: 3
  }
};

const resourceKeys: ResourceKey[] = ['hierro', 'carbon', 'madera', 'carne'];
const unitKeys: UnitKey[] = ['infantry', 'lancer', 'marksman'];
const rallyMinutes = [3, 5, 10, 15] as const;

function clampNumber(value: unknown, fallback: number, min: number, max: number): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, Math.round(numberValue)));
}

function pickBooleans<T extends string>(source: unknown, keys: T[], fallback: Record<T, boolean>): Record<T, boolean> {
  const next = { ...fallback };
  if (!source || typeof source !== 'object') return next;
  for (const key of keys) {
    const value = (source as Record<string, unknown>)[key];
    if (typeof value === 'boolean') next[key] = value;
  }
  return next;
}

export function mergeDashboardSettings(saved: unknown, fallback = defaultDashboardSettings): DashboardSettings {
  if (!saved || typeof saved !== 'object') return fallback;
  const source = saved as Record<string, any>;

  const collectResources = Array.isArray(source.collect?.resources)
    ? source.collect.resources.filter((item: unknown): item is ResourceKey => resourceKeys.includes(item as ResourceKey))
    : fallback.collect.resources;
  const huntType: HuntType = source.hunt?.huntType === 'terror' ? 'terror' : 'beast';
  const maxHuntLevel = huntType === 'terror' ? 20 : 30;
  const savedRally = Number(source.hunt?.rallyMinutes);

  return {
    autoBotModules: pickBooleans(source.autoBotModules, ['collect', 'train_troops', 'auto_hunt'], fallback.autoBotModules),
    autoBotHelp: typeof source.autoBotHelp === 'boolean' ? source.autoBotHelp : fallback.autoBotHelp,
    autoBotHeal: typeof source.autoBotHeal === 'boolean' ? source.autoBotHeal : fallback.autoBotHeal,
    autoBotShield: typeof source.autoBotShield === 'boolean' ? source.autoBotShield : fallback.autoBotShield,
    collect: {
      resources: collectResources.length ? collectResources : fallback.collect.resources,
      level: clampNumber(source.collect?.level, fallback.collect.level, 1, 10),
      marches: clampNumber(source.collect?.marches, fallback.collect.marches, 1, 6)
    },
    training: {
      units: pickBooleans(source.training?.units, unitKeys, fallback.training.units),
      amountPercent: clampNumber(source.training?.amountPercent, fallback.training.amountPercent, 5, 100)
    },
    hunt: {
      huntType,
      huntLevel: clampNumber(source.hunt?.huntLevel, fallback.hunt.huntLevel, 1, maxHuntLevel),
      marches: clampNumber(source.hunt?.marches, fallback.hunt.marches, 1, 6),
      rallyMinutes: rallyMinutes.includes(savedRally as 3 | 5 | 10 | 15)
        ? (savedRally as 3 | 5 | 10 | 15)
        : fallback.hunt.rallyMinutes
    }
  };
}

export function actionPayloadFromSettings(action: string, settings: DashboardSettings): ActionPayload {
  if (action === 'auto_bot') {
    return {
      auto_bot: {
        modules: settings.autoBotModules,
        autoHelp: settings.autoBotHelp,
        autoHeal: settings.autoBotHeal,
        autoShield: settings.autoBotShield
      },
      auto_bot_help: settings.autoBotHelp,
      auto_bot_heal: settings.autoBotHeal,
      auto_bot_shield: settings.autoBotShield,
      collect: {
        resources: settings.collect.resources.length ? settings.collect.resources : ['hierro'],
        level: settings.collect.level,
        marches: settings.collect.marches
      },
      train_troops: {
        units: settings.training.units,
        amountPercent: settings.training.amountPercent
      },
      auto_hunt: {
        huntType: settings.hunt.huntType,
        huntLevel: settings.hunt.huntLevel,
        marches: settings.hunt.marches,
        rallyMinutes: settings.hunt.rallyMinutes
      }
    };
  }
  if (action === 'collect') {
    const resources = settings.collect.resources.length ? settings.collect.resources : ['hierro'];
    return {
      collect: {
        resources,
        level: settings.collect.level,
        marches: settings.collect.marches
      }
    };
  }
  if (action === 'train_troops') {
    return {
      train_troops: {
        units: settings.training.units,
        amountPercent: settings.training.amountPercent
      }
    };
  }
  if (action === 'auto_hunt') {
    return {
      auto_hunt: {
        huntType: settings.hunt.huntType,
        huntLevel: settings.hunt.huntLevel,
        marches: settings.hunt.marches,
        rallyMinutes: settings.hunt.rallyMinutes
      }
    };
  }
  return {};
}
